import clsx from "clsx";
import React from "react";
import {
  DialogProps,
  Box,
  TextField,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  ListItem,
  ListItemText,
  ListItemIcon,
  SelectProps,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Clear } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ProgramDefaultView,
  ProgramTemplateCategory,
} from "@growth-machine-llc/stridist-api-client";

import { BaseDialog } from "./BaseDialog";
import ProgramTemplatesService from "../../services/ProgramTemplatesService";
import { COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY } from "../program-library/ProgramsTableScreen";
import { COACH_PROGRAM_LIBRARY_TEMPLATE_CURRICULUM_ROUTE } from "../../routes/routes";
import { useToastAlert } from "../app/ToastAlert/ToastAlertProvider";
import {
  ProgramDetailsViewMode,
  defaultCalendarViewOptions,
} from "../program/ProgramDetailsViewButton";
import { maybePluralize } from "../../utils/text";
import { SettingsCoverImage } from "../group-settings/SettingsCoverImage";
import { AssetType, getRandomProgramGalleryImage } from "../../constants";
import { useCurrentUserId } from "../../hooks/useCurrentUser";
import { FieldsGroup } from "../fields/FieldsGroup";
import { FieldError } from "../fields/FieldError";
import LoadingButton from "../button/LoadingButton";

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("md")]: {
      minWidth: theme.spacing(84),
    },
  },

  paper: {
    [theme.breakpoints.up("md")]: {
      minWidth: theme.spacing(84),
    },
  },

  control: {
    marginBottom: theme.spacing(4),
    width: "100%",
  },

  label: {
    fontSize: 16,
    fontWeight: 700,
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
    color: `${theme.palette.text.secondary} !important`,
  },

  fieldsRow: {
    display: "flex",
    gap: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      gap: 0,
    },
  },

  select: {
    "& > div": {
      padding: 0,
    },
  },

  selectPlaceholder: {
    color: theme.palette.text.secondary,
  },

  clearButton: {
    marginRight: theme.spacing(2),
  },

  option: {
    padding: theme.spacing(0, 2),
  },

  optionIcon: {
    color: theme.palette.text.primary,
    minWidth: theme.spacing(3),
    paddingRight: theme.spacing(1.5),
  },

  primaryText: {
    fontSize: 16,
    fontWeight: 500,
  },

  coverImage: {
    "& h4": {
      marginBottom: theme.spacing(1),
    },
  },

  submit: {
    marginTop: theme.spacing(3),
  },

  info: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },

  error: {
    position: "absolute",
  },

  header: {
    marginBottom: 0,
  },

  fieldGroupLabel: {
    marginBottom: theme.spacing(1),
  },

  settingCoverImageContainer: {
    alignItems: "flex-start",
  },

  uploadImageButton: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.secondary,
    border: `2px solid ${theme.palette.text.secondary}`,
    "&:hover": {
      backgroundColor: theme.palette.grey[100],
    },
  },
}));

const getCategoryLabel = (category: ProgramTemplateCategory): string => {
  if (category === ProgramTemplateCategory.NONE) return "Select category...";
  return category
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

const categoryOptions = Object.values(ProgramTemplateCategory);

export interface CreateProgramTemplateDialogProps extends DialogProps {
  onCreated?: (slug: string) => void;
}

export const defaultProgramUnsplashQuery = "fitness";

export function CreateProgramTemplateDialog(
  props: CreateProgramTemplateDialogProps,
) {
  const { className, onClose, onCreated, ...other } = props;
  const s = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToastAlert } = useToastAlert();
  const userId = useCurrentUserId();

  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [category, setCategory] = React.useState<ProgramTemplateCategory>(
    ProgramTemplateCategory.NONE,
  );
  const [programLength, setProgramLength] = React.useState(4);
  const [imageURL, setImageURL] = React.useState(
    getRandomProgramGalleryImage(),
  );
  const [defaultView, setDefaultView] = React.useState(
    ProgramDetailsViewMode.CALENDAR,
  );
  const [errors, setErrors] = React.useState({
    name: "",
    description: "",
  });

  const { mutate: createTemplate, isPending } = useMutation({
    mutationKey: ["create-program-template"],
    mutationFn: ProgramTemplatesService.createEmptyProgramTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY],
      });
    },
  });

  const resetState = React.useCallback(() => {
    setName("");
    setDescription("");
    setCategory(ProgramTemplateCategory.NONE);
    setProgramLength(4);
    setImageURL(getRandomProgramGalleryImage());
    setDefaultView(ProgramDetailsViewMode.CALENDAR);
    setErrors({ name: "", description: "" });
  }, []);

  const handleClose = React.useCallback(() => {
    resetState();
    onClose?.({}, "backdropClick");
  }, [onClose, resetState]);

  const handleNameChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setName(event.target.value);
      if (errors.name && event.target.value.trim().length >= 4) {
        setErrors((prev) => ({ ...prev, name: "" }));
      }
    },
    [errors.name],
  );

  const handleDescriptionChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDescription(event.target.value);
      if (errors.description && event.target.value.trim()) {
        setErrors((prev) => ({ ...prev, description: "" }));
      }
    },
    [errors.description],
  );

  const handleCategoryChange = React.useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      setCategory(event.target.value as ProgramTemplateCategory);
    },
    [],
  );

  const handleClearCategory = React.useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setCategory(ProgramTemplateCategory.NONE);
  }, []);

  const handleProgramLengthChange: SelectProps["onChange"] = React.useCallback(
    (event) => {
      setProgramLength(event.target.value as number);
    },
    [],
  );

  const handleChangeCoverImage = React.useCallback((value: string) => {
    setImageURL(value || "");
  }, []);

  const handleDefaultViewChange: SelectProps["onChange"] = React.useCallback(
    (event) => {
      setDefaultView(event.target.value as ProgramDetailsViewMode);
    },
    [],
  );

  const handleCreate = React.useCallback(() => {
    const validationErrors = { name: "", description: "" };
    let hasError = false;

    if (name.trim().length < 4) {
      validationErrors.name = "Name must be at least 4 characters long";
      hasError = true;
    }

    if (!description.trim()) {
      validationErrors.description = "Description is required";
      hasError = true;
    }

    if (hasError) {
      setErrors(validationErrors);
      return;
    }

    createTemplate(
      {
        name: name.trim(),
        description: description.trim(),
        category,
        length: programLength,
        image: imageURL,
        defaultView: defaultView.toUpperCase() as ProgramDefaultView,
      },
      {
        onSuccess({ slug }) {
          showToastAlert("success", {
            message: "Successfully created new program template.",
          });
          onCreated?.(slug);
          handleClose();
          navigate(
            COACH_PROGRAM_LIBRARY_TEMPLATE_CURRICULUM_ROUTE.replace(
              ":slug",
              slug,
            ),
          );
        },
        onError() {
          showToastAlert("error", {
            message: "Failed to create program template.",
          });
        },
      },
    );
  }, [
    name,
    description,
    category,
    programLength,
    imageURL,
    defaultView,
    createTemplate,
    showToastAlert,
    onCreated,
    handleClose,
    navigate,
  ]);

  const isSubmitDisabled = isPending;

  return (
    <BaseDialog
      className={clsx(s.root, className)}
      title="Create program template"
      closeDisabled={isPending}
      onClose={handleClose}
      PaperProps={{ className: s.paper }}
      classes={{ header: s.header }}
      {...other}
    >
      <Typography className={s.info}>
        You can change your template settings at any time later on.
      </Typography>

      <FieldsGroup
        className={s.control}
        labelClassName={s.fieldGroupLabel}
        label="Name"
      >
        <TextField
          variant="outlined"
          fullWidth
          id="name"
          placeholder="Enter template name..."
          value={name}
          onChange={handleNameChange}
          disabled={isPending}
          required
        />
        {errors.name && (
          <FieldError hideIcon className={s.error}>
            {errors.name}
          </FieldError>
        )}
      </FieldsGroup>

      <FieldsGroup
        className={s.control}
        labelClassName={s.fieldGroupLabel}
        label="Description"
      >
        <TextField
          variant="outlined"
          fullWidth
          multiline
          id="description"
          placeholder="Describe the template..."
          value={description}
          onChange={handleDescriptionChange}
          disabled={isPending}
          required
        />
        {errors.description && (
          <FieldError hideIcon className={s.error}>
            {errors.description}
          </FieldError>
        )}
      </FieldsGroup>

      <FormControl className={s.control} disabled={isPending}>
        <FormLabel
          className={s.label}
          htmlFor="category"
          component="label"
          children="Category"
        />
        <Select
          variant="outlined"
          id="category"
          value={category}
          onChange={handleCategoryChange as any}
          fullWidth
          className={s.select}
          displayEmpty
          endAdornment={
            category !== ProgramTemplateCategory.NONE && (
              <InputAdornment position="end" className={s.clearButton}>
                <IconButton
                  size="small"
                  onClick={handleClearCategory}
                  disabled={isPending}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }
          renderValue={(value) => {
            const selectedCategory = value as ProgramTemplateCategory;
            if (selectedCategory === ProgramTemplateCategory.NONE) {
              return (
                <ListItem component="div">
                  <ListItemText
                    classes={{
                      primary: clsx(s.primaryText, s.selectPlaceholder),
                    }}
                  >
                    Select category...
                  </ListItemText>
                </ListItem>
              );
            }
            return (
              <ListItem component="div">
                <ListItemText classes={{ primary: s.primaryText }}>
                  {getCategoryLabel(selectedCategory)}
                </ListItemText>
              </ListItem>
            );
          }}
        >
          {categoryOptions
            .filter((cat) => cat !== ProgramTemplateCategory.NONE)
            .map((cat) => (
              <MenuItem className={s.option} key={cat} value={cat}>
                <ListItem component="div">
                  <ListItemText classes={{ primary: s.primaryText }}>
                    {getCategoryLabel(cat)}
                  </ListItemText>
                </ListItem>
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <Box className={s.fieldsRow}>
        <FormControl className={s.control} disabled={isPending}>
          <FormLabel
            className={s.label}
            htmlFor="length"
            component="label"
            children="Length"
          />
          <Select
            variant="outlined"
            id="length"
            value={programLength}
            onChange={handleProgramLengthChange}
            fullWidth
            className={s.select}
          >
            {Array.from({ length: 99 }, (_, i) => i + 1).map((value) => (
              <MenuItem className={s.option} key={value} value={value}>
                <ListItem component="div">
                  <ListItemText classes={{ primary: s.primaryText }}>
                    {maybePluralize(value, "week")}
                  </ListItemText>
                </ListItem>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={s.control} disabled={isPending}>
          <FormLabel
            className={s.label}
            htmlFor="defaultView"
            component="label"
            children="Default view"
          />
          <Select
            className={s.select}
            variant="outlined"
            id="defaultView"
            value={defaultView}
            onChange={handleDefaultViewChange}
            fullWidth
          >
            {Object.entries(defaultCalendarViewOptions).map(
              ([viewMode, { label, icon }]) => (
                <MenuItem key={viewMode} value={viewMode} className={s.option}>
                  <ListItem component="div">
                    <ListItemIcon className={s.optionIcon}>{icon}</ListItemIcon>
                    <ListItemText classes={{ primary: s.primaryText }}>
                      {label}
                    </ListItemText>
                  </ListItem>
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>
      </Box>

      <SettingsCoverImage
        className={clsx(s.control, s.coverImage)}
        nodeId={userId}
        refType="Program"
        defaultUnsplashQuery={defaultProgramUnsplashQuery}
        assetType={AssetType.PROGRAM_IMAGE}
        image={imageURL}
        uploadImageText="Change cover"
        onImageChange={handleChangeCoverImage}
        disabled={isPending}
        classes={{
          container: s.settingCoverImageContainer,
          uploadButton: s.uploadImageButton,
        }}
        removable={false}
      >
        Give your template its own identity with a cover photo.
      </SettingsCoverImage>

      <LoadingButton
        className={s.submit}
        fullWidth
        size="large"
        variant="contained"
        onClick={handleCreate}
        disabled={isSubmitDisabled}
        loading={isPending}
      >
        Create template
      </LoadingButton>
    </BaseDialog>
  );
}
